/**
 * `useState` mirrored to Web Storage.
 *
 * Storage is untrusted input: it survives deploys, so a value written by an
 * older build (or edited by hand) can be any shape at all. Every caller must
 * therefore supply a type guard, and a value that fails it is discarded in
 * favour of the fallback rather than handed to the component. That is the whole
 * reason this exists instead of an inline `JSON.parse`.
 *
 * Writes are best-effort. Safari's private mode and a full quota both throw on
 * `setItem`, and losing a remembered panel width is never worth breaking the
 * render.
 */
import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

export type StorageKind = "local" | "session";

function area(kind: StorageKind): Storage | null {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    // Access itself throws when storage is disabled by policy.
    return null;
  }
}

function read<T>(
  kind: StorageKind,
  key: string,
  isValid: (value: unknown) => value is T,
): T | undefined {
  try {
    const raw = area(kind)?.getItem(key);
    if (raw == null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * State persisted under `key`, validated on read.
 *
 * `isValid` is only ever consulted while seeding state (a lazy initialiser, so
 * once per mount), which is why an inline arrow at the call site is safe here.
 *
 * @param isValid Type guard applied to whatever was stored. Reject anything you
 *   would not accept from a stranger — including out-of-range numbers, not
 *   merely wrong types.
 */
export function useStoredState<T>(
  key: string,
  fallback: T,
  isValid: (value: unknown) => value is T,
  kind: StorageKind = "local",
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => read(kind, key, isValid) ?? fallback);

  useEffect(() => {
    try {
      area(kind)?.setItem(key, JSON.stringify(value));
    } catch {
      /* quota exceeded / storage disabled — the in-memory value still stands */
    }
  }, [kind, key, value]);

  return [value, setValue];
}

/** Namespace every key this app owns shares. */
const APP_KEY_PREFIX = "boond.";

/**
 * Drop every piece of per-session UI state this app has stored.
 *
 * `sessionStorage` is scoped to the tab, not to the sign-in — it survives a
 * logout. Without this, signing out and signing in as someone else in the same
 * tab hands the new user the previous one's unsent draft and their conversation
 * id. Only session state is cleared: preferences that are genuinely the
 * browser's rather than the account's (theme, language, panel width) live in
 * `localStorage` and are meant to persist.
 */
export function clearSessionState(): void {
  const storage = area("session");
  if (!storage) return;
  try {
    const doomed = Object.keys(storage).filter((key) => key.startsWith(APP_KEY_PREFIX));
    doomed.forEach((key) => storage.removeItem(key));
  } catch {
    /* storage disabled — nothing was stored to begin with */
  }
}

/** Guard factory for a finite number inside an inclusive range. */
export function isNumberWithin(
  min: number,
  max: number,
): (value: unknown) => value is number {
  return (value): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

/** Guard for a boolean flag. */
export const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";

/** Guard for a persisted record id (positive integer) or `null` for "none". */
export const isIdOrNull = (value: unknown): value is number | null =>
  value === null || (typeof value === "number" && Number.isInteger(value) && value > 0);

/** Guard for free text such as an unsent draft. */
export const isString = (value: unknown): value is string => typeof value === "string";
